import ReactGA from 'react-ga4';

// Track initialization state
let isInitialized = false;

// Initialize Google Tag Manager
export const initGTM = (containerId) => {
  const GTM_CONTAINER_ID = containerId || process.env.REACT_APP_GTM_CONTAINER_ID;
  if (!GTM_CONTAINER_ID) {
    console.warn('Google Tag Manager Container ID not provided');
    return;
  }

  // Ensure dataLayer exists (this allows events to be queued before GTM loads)
  window.dataLayer = window.dataLayer || [];

  // Load GTM script if not already loaded
  if (!document.getElementById('gtm-script')) {
    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
    document.head.appendChild(script);
  }

  // Add noscript iframe
  if (!document.getElementById('gtm-noscript')) {
    const noscript = document.createElement('noscript');
    noscript.id = 'gtm-noscript';
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }
};

// Helper function to push events to GTM dataLayer
// Note: Events can be pushed before GTM script loads - they'll be processed when GTM initializes
const pushToDataLayer = (eventName, eventData = {}) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    try {
      window.dataLayer.push({
        event: eventName,
        ...eventData,
      });
    } catch (error) {
      console.error('Failed to push to dataLayer:', error);
    }
  }
};

// Initialize Google Analytics 4
export const initGA = (measurementId) => {
  const GA_MEASUREMENT_ID = measurementId || process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!GA_MEASUREMENT_ID) {
    console.warn('Google Analytics Measurement ID not provided');
    return;
  }

  try {
    // Initialize react-ga4
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      testMode: process.env.NODE_ENV === 'test',
    });
    isInitialized = true;
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
    isInitialized = false;
  }
};

// Helper function to check if GA is ready
const isGAReady = () => {
  return isInitialized && typeof ReactGA !== 'undefined';
};

// Track page views
export const trackPageView = (pageName) => {
  // Push to GTM dataLayer for Clarity integration
  // This works even before GTM script loads - events are queued and processed when GTM initializes
  pushToDataLayer('page_view', {
    page_path: window.location.pathname + window.location.search,
    page_title: pageName || document.title,
  });

  // Also send to GA4 if initialized
  if (isGAReady() && typeof window.gtag !== 'undefined') {
    try {
      // Use gtag directly for GA4 pageview
      window.gtag('event', 'page_view', {
        page_path: window.location.pathname + window.location.search,
        page_title: pageName,
      });
    } catch (error) {
      console.error('Failed to track pageview:', error);
    }
  }
};

// Track question answered events
export const trackQuestionAnswered = (questionId, questionText, answer, questionNumber, totalQuestions) => {
  const eventData = {
    category: 'Intake Form',
    question_id: questionId,
    question_text: questionText,
    answer: typeof answer === 'object' ? JSON.stringify(answer) : String(answer),
    question_number: questionNumber,
    total_questions: totalQuestions,
    progress_percentage: Math.round((questionNumber / totalQuestions) * 100),
  };

  // Push to GTM dataLayer
  pushToDataLayer('pxn_conv_intake_question_answered', eventData);

  // Also send to GA4 if initialized
  if (isGAReady()) {
    try {
      ReactGA.event('pxn_conv_intake_question_answered', eventData);
    } catch (error) {
      console.error('Failed to track question answered event:', error);
    }
  }
};

// Track form completion
export const trackFormCompleted = (totalAnswers, completionTime) => {
  const eventData = {
    category: 'Intake Form',
    total_answers: totalAnswers,
    completion_time_seconds: completionTime,
  };

  // Push to GTM dataLayer
  pushToDataLayer('pxn_conv_intake_form_completed', eventData);

  // Also send to GA4 if initialized
  if (isGAReady()) {
    try {
      ReactGA.event('pxn_conv_intake_form_completed', eventData);
    } catch (error) {
      console.error('Failed to track form completed event:', error);
    }
  }
};

// Track form abandonment
export const trackFormAbandoned = (lastQuestionAnswered, totalQuestions) => {
  const eventData = {
    category: 'Intake Form',
    last_question_answered: lastQuestionAnswered,
    total_questions: totalQuestions,
    abandonment_percentage: Math.round((lastQuestionAnswered / totalQuestions) * 100),
  };

  // Push to GTM dataLayer
  pushToDataLayer('pxn_conv_intake_form_abandoned', eventData);

  // Also send to GA4 if initialized
  if (isGAReady()) {
    try {
      ReactGA.event('pxn_conv_intake_form_abandoned', eventData);
    } catch (error) {
      console.error('Failed to track form abandoned event:', error);
    }
  }
};

// Track question editing
export const trackQuestionEdited = (questionId, questionText, oldAnswer, newAnswer) => {
  const eventData = {
    category: 'Intake Form',
    question_id: questionId,
    question_text: questionText,
    old_answer: typeof oldAnswer === 'object' ? JSON.stringify(oldAnswer) : String(oldAnswer),
    new_answer: typeof newAnswer === 'object' ? JSON.stringify(newAnswer) : String(newAnswer),
  };

  // Push to GTM dataLayer
  pushToDataLayer('pxn_conv_intake_question_edited', eventData);

  // Also send to GA4 if initialized
  if (isGAReady()) {
    try {
      ReactGA.event('pxn_conv_intake_question_edited', eventData);
    } catch (error) {
      console.error('Failed to track question edited event:', error);
    }
  }
};

// Track errors
export const trackError = (errorType, errorMessage, questionId) => {
  const eventData = {
    category: 'Intake Form',
    error_type: errorType,
    error_message: errorMessage,
    question_id: questionId || null,
  };

  // Push to GTM dataLayer
  pushToDataLayer('pxn_conv_intake_error', eventData);

  // Also send to GA4 if initialized
  if (isGAReady()) {
    try {
      ReactGA.event('pxn_conv_intake_error', eventData);
    } catch (error) {
      console.error('Failed to track error event:', error);
    }
  }
};
